import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_ORDERS } from '../../lib/mockData';
import OrderDetailView from './OrderDetailView';

const GROUP_STATUS_LABELS = {
  awaiting_payment: 'Awaiting Payment',
  paid:             'Payment Confirmed',
  preparing:        'Preparing Order',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Delivered',
  cancelled:        'Cancelled',
};

const GROUP_STATUS_COLORS = {
  awaiting_payment: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  paid:             'bg-green-50 text-green-700 border-green-100',
  preparing:        'bg-orange-50 text-orange-700 border-orange-100',
  out_for_delivery: 'bg-teal-50 text-teal-700 border-teal-100',
  delivered:        'bg-sage-50 text-sage-700 border-sage-100',
  cancelled:        'bg-red-50 text-red-600 border-red-100',
};

const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

function cooldownRemaining(lastResentAt) {
  if (!lastResentAt) return 0;
  return Math.max(0, new Date(lastResentAt).getTime() + COOLDOWN_MS - Date.now());
}

function formatCooldown(ms) {
  const h = Math.floor(ms / (60 * 60 * 1000));
  const m = Math.ceil((ms % (60 * 60 * 1000)) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function ResendButton({ item, onResent }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const remaining = cooldownRemaining(item.last_resent_at);
  const onCooldown = remaining > 0;

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('resend-payment-link', {
        body: { itemId: item.id },
      });
      if (fnError || data?.error) throw new Error(fnError?.message || data?.error);
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      if (onResent) onResent();
    } catch (err) {
      setError(err.message || 'Could not resend. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
        <SafeIcon name="CheckCircle" className="text-sm" /> Sent!
      </span>
    );
  }

  if (error) {
    return <span className="text-xs text-red-500 max-w-[160px] text-right">{error}</span>;
  }

  return (
    <button
      onClick={handleResend}
      disabled={loading || onCooldown}
      title={onCooldown ? `Can resend in ${formatCooldown(remaining)}` : 'Resend payment link'}
      className={`text-xs font-semibold flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
        onCooldown
          ? 'text-sage-300 cursor-not-allowed'
          : 'text-blush-400 hover:text-blush-600 hover:bg-blush-50'
      }`}
    >
      {loading
        ? <><SafeIcon name="Loader" className="text-sm animate-spin" /> Sending…</>
        : onCooldown
        ? <><SafeIcon name="Clock" className="text-sm" /> {formatCooldown(remaining)}</>
        : <><SafeIcon name="Send" className="text-sm" /> Resend</>
      }
    </button>
  );
}

function GroupOrderCard({ order, onResent }) {
  const items = order.group_order_items || [];
  const paidCount = items.filter(i => i.payment_status === 'paid').length;
  const totalCount = items.length;
  const allPaid = paidCount === totalCount && totalCount > 0;

  return (
    <div className="glass-panel p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-1">
            Group Order #{order.id.substring(0, 8)}
          </p>
          <p className="text-sm text-charcoal font-medium">
            {new Date(order.delivery_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          <p className="text-xs text-sage-400 mt-0.5">{order.delivery_address}</p>
        </div>
        <span className={`self-start sm:self-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${GROUP_STATUS_COLORS[order.status] || 'bg-sage-50 text-sage-600 border-sage-100'}`}>
          {GROUP_STATUS_LABELS[order.status] || order.status}
        </span>
      </div>

      {/* Payment progress */}
      <div className="border-t border-sage-50 pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Payment Progress</p>
          <span className={`text-xs font-bold ${allPaid ? 'text-green-600' : 'text-yellow-600'}`}>
            {paidCount}/{totalCount} paid
          </span>
        </div>
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-charcoal truncate">{item.recipient_name}</span>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  item.payment_status === 'paid'
                    ? 'bg-green-50 text-green-700'
                    : item.payment_status === 'expired'
                    ? 'bg-red-50 text-red-600'
                    : 'bg-yellow-50 text-yellow-700'
                }`}>
                  {item.payment_status === 'paid' ? 'Paid' : item.payment_status === 'expired' ? 'Expired' : 'Pending'}
                </span>
                {item.payment_status !== 'paid' && (
                  <ResendButton item={item} onResent={onResent} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const STATUS_LABELS = {
  pending:          'Request Received',
  paid:             'Payment Confirmed',
  preparing:        'Preparing Order',
  out_for_delivery: 'Out for Delivery',
  delivered:        'Hand Delivered',
  cancelled:        'Cancelled',
};

const STATUS_COLORS = {
  pending:          'bg-yellow-50 text-yellow-700 border-yellow-100',
  paid:             'bg-green-50 text-green-700 border-green-100',
  preparing:        'bg-orange-50 text-orange-700 border-orange-100',
  out_for_delivery: 'bg-teal-50 text-teal-700 border-teal-100',
  delivered:        'bg-sage-50 text-sage-700 border-sage-100',
  cancelled:        'bg-red-50 text-red-600 border-red-100',
};

function OrderCard({ order, onClick }) {
  const item = order.order_items?.[0] || {};
  return (
    <motion.div
      whileHover={{ y: -4 }}
      onClick={() => onClick(order)}
      className="glass-panel p-6 cursor-pointer group transition-all"
    >
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="w-full sm:w-32 h-32 rounded-[2rem] overflow-hidden shrink-0 border border-sage-100 shadow-sm">
          {item.product_image_url ? (
            <img
              src={item.product_image_url}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              alt={item.product_name}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl bg-sage-50">🥙</div>
          )}
        </div>
        <div className="flex-1 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-sage-400 uppercase tracking-widest mb-1">Order #{order.id.substring(0, 8)}</p>
              <h3 className="font-serif text-xl text-charcoal">{item.product_name || 'Custom Vase'}</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[order.status]}`}>
              {STATUS_LABELS[order.status]}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-sage-50">
            <div>
              <p className="text-[10px] text-sage-400 font-bold uppercase mb-0.5">Date</p>
              <p className="text-sm font-bold text-charcoal">{new Date(order.delivery_date).toLocaleDateString()}</p>
            </div>
            <div className="text-right col-span-1 md:col-start-4">
              <p className="text-[10px] text-sage-400 font-bold uppercase mb-0.5">Price</p>
              <p className="text-sm font-bold text-blush-500">${item.price || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function CustomerPortal() {
  const [orders, setOrders] = useState([]);
  const [groupOrders, setGroupOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userId, setUserId] = useState(null);

  const fetchOrders = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    if (isSupabaseConfigured()) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
          const [ordersResult, groupOrdersResult] = await Promise.all([
            supabase
              .from('orders')
              .select('*, order_items(*), order_photos(*)')
              .eq('customer_id', user.id)
              .order('created_at', { ascending: false }),
            supabase
              .from('group_orders')
              .select('*, group_order_items(id, recipient_name, recipient_email, item_total_cents, payment_status, last_resent_at, resend_count)')
              .eq('coordinator_id', user.id)
              .order('created_at', { ascending: false }),
          ]);
          if (ordersResult.data) {
            setOrders(ordersResult.data);
            setSelectedOrder(prev => prev ? ordersResult.data.find(o => o.id === prev.id) || prev : null);
          }
          if (groupOrdersResult.data) {
            setGroupOrders(groupOrdersResult.data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setOrders(MOCK_ORDERS);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Set up realtime subscription once we have the userId
  // so we only react to this customer's order changes
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`portal-updates-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `customer_id=eq.${userId}`
        },
        () => fetchOrders(true)
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_photos'
        },
        () => fetchOrders(true)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'group_orders',
          filter: `coordinator_id=eq.${userId}`
        },
        () => fetchOrders(true)
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'group_order_items'
        },
        () => fetchOrders(true)
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [userId]);

  if (loading) return (
    <div className="flex justify-center py-20 w-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300"></div>
    </div>
  );

  return (
    <div className="w-full px-4 md:px-8 lg:px-12 py-12">
      <AnimatePresence mode="wait">
        {selectedOrder ? (
          <OrderDetailView
            key="detail"
            order={selectedOrder}
            onBack={() => setSelectedOrder(null)}
            onUpdate={() => fetchOrders(true)}
          />
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-12"
          >
            <div className="space-y-2">
              <h1 className="text-4xl md:text-5xl font-serif text-charcoal">Order History</h1>
              <p className="text-sage-500 text-lg font-medium">Track your arrangements and delivery status.</p>
            </div>

            {groupOrders.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-serif text-charcoal">Group Orders</h2>
                <div className="grid grid-cols-1 gap-6">
                  {groupOrders.map(order => <GroupOrderCard key={order.id} order={order} onResent={() => fetchOrders(true)} />)}
                </div>
              </div>
            )}

            {orders.length > 0 ? (
              <div className="space-y-4">
                {groupOrders.length > 0 && (
                  <h2 className="text-2xl font-serif text-charcoal">Individual Orders</h2>
                )}
                <div className="grid grid-cols-1 gap-6">
                  {orders.map(order => <OrderCard key={order.id} order={order} onClick={setSelectedOrder} />)}
                </div>
              </div>
            ) : groupOrders.length === 0 ? (
              <div className="glass-panel p-20 text-center space-y-6">
                <SafeIcon name="ShoppingBag" className="text-6xl text-sage-100 mx-auto" />
                <h2 className="text-2xl font-serif text-charcoal">No orders found</h2>
                <Link to="/" className="btn-primary">Place Your First Order</Link>
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
